/** Slug helper for manufacturing capability option values stored in JSON. */
export const capabilityOptionSlug = (label) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const toOptions = (entries) =>
  entries.map(({ value, label }) => ({ value, label }));

const labelsToOptions = (labels) =>
  labels.map((label) => ({ value: capabilityOptionSlug(label), label }));

/** Manufacturing Process Type * (31 industry categories from design) */
export const MANUFACTURING_PROCESS_TYPE_LABELS = [
  'Additive Manufacturing (3D)',
  'Automotive & Aerospace Components',
  'Batteries & Energy Packs',
  'Casting & foundry',
  'Cement /Concrete / Precast',
  'Chemicals & Petrochemicals ( Toll Processing)',
  'Composites Processing',
  'Electronics ( PCB Fabrication& SMT/TH)',
  'food & Beverage Processing',
  'Glass /Ceramics/ Stones & Tiles',
  'Heat treatment',
  'Leather & footwear',
  'Medical Devices & Cleanroom Assembly',
  'Metails fabrication& Machining',
  'Nutraceuticals & Dietary Supplements',
  'Other Manufactring(general)',
  'Packaging Conversion & printing',
  'Paper /Pulp/ tissue',
  'Pharma/ Biotech/ Nutraceuticals',
  'Plasics & POlymer Moulding',
  'Recycling & Waste Processing',
  'Rubber & Elastomer Products',
  'Semiconductors ( Wafer & IC Packaging)',
  'Sheet Metal Forming & Stamping',
  'Surface Finishing & Coatings',
  'Textiles& Apparel',
  'Thermoforming & Blow/Rotational Moulding',
  'Tools, Moulds & dies',
  'Wood & furniture',
  'Cosmetics Personal & Home Care',
  'Contract Packing & co-packing',
];

export const MANUFACTURING_PROCESS_TYPE_OPTIONS = labelsToOptions(MANUFACTURING_PROCESS_TYPE_LABELS);

/** @deprecated Use MANUFACTURING_PROCESS_TYPE_OPTIONS */
export const MANUFACTURING_PROCESSES_TYPE_OPTIONS = MANUFACTURING_PROCESS_TYPE_OPTIONS;

/** Processes * */
export const PROCESS_OPTIONS = [
  { value: 'cnc_turning', label: 'CNC turning' },
  { value: 'cnc_milling', label: 'CNC milling' },
  { value: 'drilling_tapping', label: 'drilling/tapping' },
  { value: 'laser_plasma_waterjet_cutting', label: 'laser/plasma/waterjet cutting' },
  { value: 'press_brake_bending', label: 'press-brake bending' },
  { value: 'welding_mig_tig_spot', label: 'welding (MIG/TIG/spot)' },
  { value: 'deburr_polish', label: 'deburr/polish' },
  { value: 'bending', label: 'Bending' },
  { value: 'general_fabrication', label: 'General Fabrication' },
  { value: 'cnc_machining', label: 'CNC Machining' },
];

/** Label (23-item checklist from design) */
export const LABEL_OPTIONS = [
  { value: 'design_dfm', label: 'Design/DFM' },
  { value: 'prototyping', label: 'Prototyping' },
  { value: 'tooling_moulds_dies', label: 'Tooling/Moulds/Dies' },
  { value: 'small_batch_custom', label: 'Small-batch/Custom' },
  { value: 'mass_production', label: 'Mass Production' },
  { value: 'assembly_box_build', label: 'Assembly/Box Build' },
  { value: 'automation_robotics', label: 'Automation/ Robotics' },
  { value: 'testing_qa_metrology', label: 'Testing/QA/Metrology' },
  { value: 'ndt_ut_rt_pt_mt', label: 'NDT (UT/RT/PT/MT/etc.)' },
  { value: 'cleanroom_capability', label: 'Cleanroom Capability' },
  { value: 'sterile_aseptic', label: 'Sterile/ Aseptic' },
  { value: 'food_grade_haccp', label: 'Food-grade/HACCP' },
  { value: 'gmp_pharma_grade', label: 'GMP / Pharma-grade' },
  { value: 'serialization_track_trace_mes', label: 'Serialization/Track & Trace Digital Traceability / MES' },
  { value: 'iso_9001', label: 'ISO 9001' },
  { value: 'iatf_16949_automotive', label: 'IATF 16949 (Automotive)' },
  { value: 'as9100_aerospace', label: 'AS9100 (Aerospace)' },
  { value: 'iso_13485_medical_devices', label: 'ISO 13485 (Medical Devices)' },
  { value: 'iso_22000_fssc_22000', label: 'ISO 22000/FSSC 22000' },
  { value: 'ipc_a610_whma_other_ipc', label: 'IPC -A-610/WHMA/Other IPC' },
  { value: 'ehs_hazardous_handling', label: 'EHS/Hazardous Handling' },
  { value: 'cold_chain', label: 'Cold Chain' },
  { value: 'export_ready_compliance', label: 'Export-Ready Compliance' },
];

/** Technical Expertise (Design / Prototyping / Mass Production / Customization) */
export const TECHNICAL_EXPERTISE_OPTIONS = [
  { value: 'design', label: 'Design' },
  { value: 'prototyping', label: 'Prototyping' },
  { value: 'mass_production', label: 'Mass Production' },
  { value: 'customization', label: 'Customization' },
];

/** @deprecated Use LABEL_OPTIONS */
export const TECHNICAL_EXPERTISE_LABEL_OPTIONS = LABEL_OPTIONS;

/** Batch Size * */
export const BATCH_SIZE_OPTIONS = [
  { value: '1', label: '1 unit' },
  { value: '5', label: '5 units' },
  { value: '10', label: '10 units' },
  { value: '25', label: '25 units' },
  { value: '50', label: '50 units' },
  { value: '100', label: '100 units' },
  { value: '250', label: '250 units' },
  { value: '500', label: '500 units' },
  { value: '1000', label: '1,000 units' },
];

/** Machine capacity unit + period */
export const MACHINE_CAPACITY_UNIT_OPTIONS = [
  { value: 'kilograms', label: 'Kilograms' },
  { value: 'tonnes', label: 'Tonnes' },
  { value: 'pounds', label: 'Pounds' },
];

export const MACHINE_CAPACITY_PERIOD_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
];

/** Operational Status (multi-select) */
export const OPERATIONAL_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'in_active', label: 'In Active' },
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'available', label: 'Available' },
  { value: 'leased', label: 'Leased' },
];

const ALL_CAPABILITY_OPTIONS = [
  ...MANUFACTURING_PROCESS_TYPE_OPTIONS,
  ...PROCESS_OPTIONS,
  ...LABEL_OPTIONS,
  ...TECHNICAL_EXPERTISE_OPTIONS,
  ...BATCH_SIZE_OPTIONS,
  ...MACHINE_CAPACITY_UNIT_OPTIONS,
  ...MACHINE_CAPACITY_PERIOD_OPTIONS,
  ...OPERATIONAL_STATUS_OPTIONS,
];

export const CAPABILITY_VALUE_TO_LABEL = Object.fromEntries(
  ALL_CAPABILITY_OPTIONS.map(({ value, label }) => [value, label]),
);

/** Resolve stored slug/value to display label; falls back to humanized string. */
export const capabilityValueToLabel = (value) => {
  if (value == null || value === '') return '';
  const key = String(value).trim();
  if (CAPABILITY_VALUE_TO_LABEL[key]) return CAPABILITY_VALUE_TO_LABEL[key];
  if (key.includes(' ') || /[A-Z]/.test(key)) return key;
  return key.replace(/_/g, ' ');
};
